import type { Job } from '@cibli/shared';

import { Card, cn } from '@cibli/shared';
import { Briefcase, Calendar, Check, Lightbulb, MapPin, Target } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractTypeBadge } from '@/components/molecules/ContractTypeBadge';
import { EmploymentTypeBadge } from '@/components/molecules/EmploymentTypeBadge.tsx';
import { formatDate } from '@/utils/dateFormatters';

type JobCardProps = {
  job: Job;
  onClick: () => void;
  showExperience?: boolean;
  showPublicationDate?: boolean;
  hasApplied?: boolean;
  className?: string;
};

export const JobCard = forwardRef<HTMLDivElement, JobCardProps>(function JobCard(
  {
    job,
    onClick,
    showExperience = false,
    showPublicationDate = false,
    hasApplied = false,
    className,
  },
  ref,
) {
  const { t, i18n } = useTranslation();

  const handleClick = () => {
    onClick();
  };

  const matchesJob = job.matchesJob ?? false;
  const matchesDistance = job.matchesDistance ?? false;

  return (
    <div ref={ref} className={className}>
      <Card
        className={cn('shadow-xl p-6 cursor-pointer relative', hasApplied && 'opacity-60')}
        onClick={handleClick}
      >
        {hasApplied && (
          <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-cj-green/10 text-cj-green-nuance border border-cj-green/20 text-[11px] font-semibold px-2.5 py-0.5 rounded-full">
            <Check className="w-3 h-3" />
            {t('dashboard.jobs.alreadyApplied')}
          </div>
        )}
        <div className="flex flex-row gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 items-center">
              <div>{job.companyName}</div>
              <div className="text-primary">{job.city}</div>
            </div>
            <div className="text-lg font-semibold">{job.title}</div>
            <div className="flex flex-row gap-2 items-center flex-wrap">
              {matchesJob ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <Target className="w-3.5 h-3.5" />
                  {t('MatchingJobs.badges.jobMatch')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                  <Lightbulb className="w-3.5 h-3.5" />
                  {t('MatchingJobs.badges.otherOpportunity')}
                </span>
              )}
              {matchesDistance && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-sky-100 text-sky-800 border border-sky-200">
                  <MapPin className="w-3.5 h-3.5" />
                  {t('MatchingJobs.badges.distanceMatch')}
                </span>
              )}
              {job.contractType && <ContractTypeBadge contractType={job.contractType} />}
              {job.employmentTypes?.map(type => (
                <EmploymentTypeBadge key={type} employmentType={type} />
              ))}
            </div>
            {job.expectedStartDate && (
              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {t('MatchingJobs.starting', { date: job.expectedStartDate })}
              </div>
            )}
            {showExperience && job.experience !== null && job.experience !== undefined && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Briefcase className="w-4 h-4" />
                {job.experience === 0
                  ? t('dashboard.jobs.details.noExperience')
                  : t('dashboard.jobs.experience', { years: job.experience })}
              </div>
            )}
            {showPublicationDate && job.updatedAt && (
              <div className="text-xs text-muted-foreground">
                {t('dashboard.jobs.updatedAt', { date: formatDate(job.updatedAt, i18n.language) })}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
});
